import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useGetAdCategoryQuery, useGetCustomFiltersQuery, useCreateCustomFilterMutation, useUpdateCustomFilterMutation, useDeleteCustomFilterMutation } from '../../../store/api/adminApi';
import PageLoader from '../../../components/PageLoader';
import Modal from '../../../components/Modal';
import { showSuccess, showError } from '../../../utils/toast';
import { confirmAction } from '../../../utils/confirm';
import { useLanguage } from '../../../context/LanguageContext';

interface CustomFilterRow {
  id?: number;
  filter_name: string;
  filter_name_ar: string;
  filter_key?: string;
  filter_type: 'text' | 'number' | 'date' | 'dropdown_single' | 'dropdown_multiple' | 'range' | 'checkbox' | 'radio' | 'countries' | 'cities' | 'currency';
  options: Array<{ en: string; ar: string }>;
  min_value: number | null;
  max_value: number | null;
  sort_order: number;
}

const AdminAdCategoryCustomFilters: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useLanguage();
  const { id } = useParams<{ id: string }>();
  const categoryId = parseInt(id!);
  const [searchParams] = useSearchParams();
  const canEdit = searchParams.get('can_edit') === '1';

  const { data: categoryData, isLoading: isLoadingCategory } = useGetAdCategoryQuery(categoryId);
  const { data: filtersData, isLoading: isLoadingFilters, refetch } = useGetCustomFiltersQuery(categoryId);
  const [createFilter] = useCreateCustomFilterMutation();
  const [updateFilter] = useUpdateCustomFilterMutation();
  const [deleteFilter] = useDeleteCustomFilterMutation();

  const [filters, setFilters] = useState<CustomFilterRow[]>([]);
  
  // Modal State
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingFilter, setEditingFilter] = useState<CustomFilterRow | null>(null);

  const category = categoryData?.data;

  useEffect(() => {
    if (filtersData?.data) {
      const filtersList = filtersData.data.map((filter: any) => ({
        id: filter.id,
        filter_name: filter.filter_name || '',
        filter_name_ar: filter.filter_name_ar || '',
        filter_key: filter.filter_key || '',
        filter_type: filter.filter_type || 'text',
        options: filter.options || [],
        min_value: filter.min_value ?? null,
        max_value: filter.max_value ?? null,
        sort_order: filter.sort_order || 0,
      }));
      setFilters(filtersList);
    } else {
      setFilters([]);
    }
  }, [filtersData]);

  const openAddModal = () => {
    setEditingFilter({
      filter_name: '',
      filter_name_ar: '',
      filter_type: 'text',
      options: [],
      min_value: null,
      max_value: null,
      sort_order: filters.length,
    });
    setIsModalOpen(true);
  };

  const openEditModal = (filter: CustomFilterRow) => {
    setEditingFilter(JSON.parse(JSON.stringify(filter))); // Deep copy
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setEditingFilter(null);
    setIsModalOpen(false);
  };

  const handleModalInputChange = (field: keyof CustomFilterRow, value: any) => {
    if (!editingFilter) return;
    setEditingFilter(prev => {
      const updated = { ...prev!, [field]: value };
      if (field === 'filter_type') {
        if (value !== 'dropdown_single' && value !== 'dropdown_multiple' && value !== 'radio' && value !== 'checkbox') {
          updated.options = [];
        }
        if (value !== 'range') {
          updated.min_value = null;
          updated.max_value = null;
        }
      }
      return updated;
    });
  };

  const handleModalOptionChange = (optionIndex: number, lang: 'en' | 'ar', value: string) => {
    if (!editingFilter) return;
    setEditingFilter(prev => {
      const updated = { ...prev! };
      const options = [...updated.options];
      
      if (!options[optionIndex]) {
        options[optionIndex] = { en: '', ar: '' };
      } else {
        options[optionIndex] = { ...options[optionIndex] };
      }
      
      options[optionIndex][lang] = value;
      updated.options = options;
      return updated;
    });
  };

  const addModalOption = () => {
    if (!editingFilter) return;
    setEditingFilter(prev => ({
      ...prev!,
      options: [...prev!.options, { en: '', ar: '' }]
    }));
  };

  const removeModalOption = (optionIndex: number) => {
    if (!editingFilter) return;
    setEditingFilter(prev => ({
      ...prev!,
      options: prev!.options.filter((_, i) => i !== optionIndex)
    }));
  };

  const handleSaveFilter = async () => {
    if (!editingFilter) return;

    if (!editingFilter.filter_name || !editingFilter.filter_name_ar) {
      showError(t('Filter name (English and Arabic) is required'));
      return;
    }

    if ((editingFilter.filter_type === 'dropdown_single' || editingFilter.filter_type === 'dropdown_multiple' || editingFilter.filter_type === 'radio' || editingFilter.filter_type === 'checkbox') && editingFilter.options.length === 0) {
      showError(t('Please add at least one option for dropdown/radio/checkbox filters'));
      return;
    }

    if (editingFilter.filter_type === 'dropdown_single' || editingFilter.filter_type === 'dropdown_multiple' || editingFilter.filter_type === 'radio' || editingFilter.filter_type === 'checkbox') {
      const hasEmptyOptions = editingFilter.options.some(opt => !opt.en || !opt.ar);
      if (hasEmptyOptions) {
        showError(t('All options must have both English and Arabic values'));
        return;
      }
    }

    if (editingFilter.filter_type === 'range') {
      const min = editingFilter.min_value;
      const max = editingFilter.max_value;
      if (min !== null && max !== null && max < min) {
        showError(t('Max value cannot be less than min value'));
        return;
      }
    }

    try {
      const filterData = {
        category_id: categoryId,
        filter_name: editingFilter.filter_name,
        filter_name_ar: editingFilter.filter_name_ar,
        filter_type: editingFilter.filter_type,
        options: (editingFilter.filter_type === 'dropdown_single' || editingFilter.filter_type === 'dropdown_multiple' || editingFilter.filter_type === 'radio' || editingFilter.filter_type === 'checkbox') ? editingFilter.options : null,
        min_value: editingFilter.filter_type === 'range' ? editingFilter.min_value : null,
        max_value: editingFilter.filter_type === 'range' ? editingFilter.max_value : null,
        sort_order: editingFilter.sort_order,
      };

      if (editingFilter.id) {
        await updateFilter({ id: editingFilter.id, data: filterData }).unwrap();
        showSuccess(t('Custom filter updated successfully'));
      } else {
        await createFilter(filterData).unwrap();
        showSuccess(t('Custom filter created successfully'));
      }

      closeModal();
      refetch();
    } catch (err: any) {
      showError(err?.data?.message || t('Failed to save custom filter. Please try again.'));
    }
  };

  const handleDeleteFilter = async (filter: CustomFilterRow) => {
    if (!filter.id) return;

    const confirmed = await confirmAction(
      t('Delete Custom Filter'),
      t('Are you sure you want to delete this custom filter?')
    );

    if (!confirmed) return;

    try {
      await deleteFilter(filter.id).unwrap();
      showSuccess(t('Custom filter deleted successfully'));
      refetch();
    } catch (err: any) {
      showError(err?.data?.message || t('Failed to delete custom filter. Please try again.'));
    }
  };

  if (isLoadingCategory || isLoadingFilters) {
    return <PageLoader />;
  }

  return (
    <div className="app-main flex-column flex-row-fluid">
      <div className="d-flex flex-column flex-column-fluid">
        <div id="kt_app_toolbar" className="app-toolbar py-3 py-lg-6">
          <div id="kt_app_toolbar_container" className="app-container container-xxl d-flex flex-stack">
            <div className="page-title d-flex flex-column justify-content-center flex-wrap me-3">
              <h1 className="page-heading d-flex text-dark fw-bold fs-3 flex-column justify-content-center my-0">
                {t('Custom Filters')}
              </h1>
              <ul className="breadcrumb breadcrumb-separatorless fw-semibold fs-7 my-0 pt-1">
                <li className="breadcrumb-item text-muted">
                  <a href="#" onClick={(e) => { e.preventDefault(); navigate('/admin/ad-categories'); }} className="text-muted text-hover-primary">
                    {t('Ad Categories')}
                  </a>
                </li>
                {category?.parent_id && (
                  <>
                    <li className="breadcrumb-item">
                      <span className="bullet bg-gray-400 w-5px h-2px"></span>
                    </li>
                    <li className="breadcrumb-item text-muted">
                      <a href="#" onClick={(e) => { e.preventDefault(); navigate(`/admin/ad-categories?parent_id=${category.parent_id}`); }} className="text-muted text-hover-primary">
                        {category.parent?.title || t('Parent Category')}
                      </a>
                    </li>
                  </>
                )}
                <li className="breadcrumb-item">
                  <span className="bullet bg-gray-400 w-5px h-2px"></span>
                </li>
                <li className="breadcrumb-item text-muted">
                  {category?.title || t('Category')} - {t('Custom Filters')}
                </li>
              </ul>
            </div>
            <div className="d-flex align-items-center gap-2 gap-lg-3">
              <button
                type="button"
                className="btn btn-sm btn-light"
                onClick={() => {
                  if (category?.parent_id) {
                    navigate(`/admin/ad-categories?parent_id=${category.parent_id}`);
                  } else {
                    navigate('/admin/ad-categories');
                  }
                }}
              >
                {t('Back')}
              </button>
              {canEdit &&
                <button
                  type="button"
                  className="btn btn-sm btn-primary"
                  onClick={openAddModal}
                >
                  {t('Add Custom Filter')}
                </button>
              }
            </div>
          </div>
        </div>

        <div id="kt_app_content" className="app-content flex-column-fluid">
          <div id="kt_app_content_container" className="app-container container-xxl">
            <div className="card">
              <div className="card-header border-0 pt-6">
                <div className="card-title">
                  <h3 className="card-title align-items-start flex-column">
                    <span className="card-label fw-bold fs-3 mb-1">{t('Custom Filters for:')} {category?.title || t('Category')}</span>
                    <span className="text-muted mt-1 fw-semibold fs-7">{t('Manage custom filters for this ad category')}</span>
                  </h3>
                </div>
              </div>
              <div className="card-body pt-0">
                {filters.length === 0 ? (
                  <div className="text-center py-10">
                    <p className="text-muted">{t('No custom filters found. Click "Add Custom Filter" to create one.')}</p>
                  </div>
                ) : (
                  <div className="table-responsive">
                    <table className="table align-middle table-row-dashed fs-6 gy-5">
                      <thead>
                        <tr className="text-start text-muted fw-bold fs-7 text-uppercase gs-0">
                          <th className="min-w-100px">{t('Filter Name (EN)')}</th>
                          <th className="min-w-100px">{t('Filter Name (AR)')}</th>
                          <th className="min-w-100px">{t('Filter Key')}</th>
                          <th className="min-w-100px">{t('Type')}</th>
                          <th className="min-w-100px">{t('Options')}</th>
                          <th className="min-w-80px">{t('Min Value')}</th>
                          <th className="min-w-80px">{t('Max Value')}</th>
                          <th className="min-w-50px">{t('Sort Order')}</th>
                          <th className="text-end min-w-100px">{t('Actions')}</th>
                        </tr>
                      </thead>
                      <tbody className="text-gray-600 fw-semibold">
                        {filters.map((filter, index) => (
                          <tr key={filter.id || index}>
                            <td>{filter.filter_name}</td>
                            <td>{filter.filter_name_ar}</td>
                            <td>
                              <span className="badge badge-light-info">{filter.filter_key}</span>
                            </td>
                            <td>
                              <span className="badge badge-light-primary">{filter.filter_type}</span>
                            </td>
                            <td>
                              {(filter.filter_type === 'dropdown_single' || filter.filter_type === 'dropdown_multiple' || filter.filter_type === 'radio' || filter.filter_type === 'checkbox') ? (
                                <div className="d-flex flex-wrap gap-1">
                                  {filter.options.map((opt, i) => (
                                    <span key={i} className="badge badge-light-secondary text-gray-700">
                                      {opt.en}
                                    </span>
                                  ))}
                                </div>
                              ) : '-'}
                            </td>
                            <td>{filter.filter_type === 'range' && filter.min_value !== null ? filter.min_value : '-'}</td>
                            <td>{filter.filter_type === 'range' && filter.max_value !== null ? filter.max_value : '-'}</td>
                            <td>{filter.sort_order}</td>
                            <td className="text-end">
                              {canEdit && (
                                <div className="d-flex justify-content-end gap-2">
                                  <button
                                    className="btn btn-icon btn-sm btn-light-primary"
                                    onClick={() => openEditModal(filter)}
                                  >
                                    <i className="fas fa-pencil-alt fs-6"></i>
                                  </button>
                                  <button
                                    className="btn btn-icon btn-sm btn-light-danger"
                                    onClick={() => handleDeleteFilter(filter)}
                                  >
                                    <i className="fas fa-trash fs-6"></i>
                                  </button>
                                </div>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      <Modal
        isOpen={isModalOpen}
        onClose={closeModal}
        title={editingFilter?.id ? t('Edit Custom Filter') : t('Add Custom Filter')}
        size="lg"
        footer={
          <div className="d-flex justify-content-end gap-2">
            <button
              type="button"
              className="btn btn-light"
              onClick={closeModal}
            >
              {t('Cancel')}
            </button>
            <button
              type="button"
              className="btn btn-primary"
              onClick={handleSaveFilter}
            >
              {t('Save Changes')}
            </button>
          </div>
        }
      >
        {editingFilter && (
          <div className="d-flex flex-column gap-4">
            <div className="row">
              <div className="col-md-6">
                <label className="form-label required">{t('Filter Name (English)')}</label>
                <input
                  type="text"
                  className="form-control"
                  value={editingFilter.filter_name}
                  onChange={(e) => handleModalInputChange('filter_name', e.target.value)}
                  placeholder={t('Enter filter name in English')}
                />
              </div>
              <div className="col-md-6">
                <label className="form-label required">{t('Filter Name (Arabic)')}</label>
                <input
                  type="text"
                  className="form-control"
                  value={editingFilter.filter_name_ar}
                  onChange={(e) => handleModalInputChange('filter_name_ar', e.target.value)}
                  placeholder={t('Enter filter name in Arabic')}
                />
              </div>
            </div>

            <div className="row">
              <div className="col-md-6">
                <label className="form-label required">{t('Filter Type')}</label>
                <select
                  className="form-select"
                  value={editingFilter.filter_type}
                  onChange={(e) => handleModalInputChange('filter_type', e.target.value)}
                >
                  <option value="text">{t('Text')}</option>
                  <option value="number">{t('Number')}</option>
                  <option value="date">{t('Date')}</option>
                  <option value="checkbox">{t('Checkbox')}</option>
                  <option value="radio">{t('Radio')}</option>
                  <option value="dropdown_single">{t('Dropdown Single')}</option>
                  <option value="dropdown_multiple">{t('Dropdown Multiple')}</option>
                  <option value="range">{t('Range')}</option>
                  <option value="countries">{t('Country list')}</option>
                  <option value="cities">{t('City list')}</option>
                  <option value="currency">{t('Currency list')}</option>

                </select>
              </div>
              <div className="col-md-6">
                <label className="form-label">{t('Sort Order')}</label>
                <input
                  type="number"
                  className="form-control"
                  value={editingFilter.sort_order}
                  onChange={(e) => handleModalInputChange('sort_order', parseInt(e.target.value) || 0)}
                />
              </div>
            </div>

            {editingFilter.filter_type === 'range' && (
              <div className="row">
                <div className="col-md-6">
                  <label className="form-label">{t('Min Value')}</label>
                  <input
                    type="number"
                    className="form-control"
                    value={editingFilter.min_value ?? ''}
                    onChange={(e) => {
                      const value = e.target.value;
                      handleModalInputChange('min_value', value === '' ? null : Number(value));
                    }}
                    placeholder={t('No min value')}
                  />
                </div>
                <div className="col-md-6">
                  <label className="form-label">{t('Max Value')}</label>
                  <input
                    type="number"
                    className="form-control"
                    value={editingFilter.max_value ?? ''}
                    onChange={(e) => {
                      const value = e.target.value;
                      handleModalInputChange('max_value', value === '' ? null : Number(value));
                    }}
                    placeholder={t('No max value')}
                  />
                </div>
              </div>
            )}

            {(editingFilter.filter_type === 'dropdown_single' || editingFilter.filter_type === 'dropdown_multiple' || editingFilter.filter_type === 'radio' || editingFilter.filter_type === 'checkbox') && (
              <div className="d-flex flex-column gap-2 mt-2">
                <label className="form-label required">{t('Options')}</label>
                <div className="table-responsive">
                  <table className="table table-row-dashed table-row-gray-300 gy-3">
                    <thead>
                      <tr className="fw-bold fs-6 text-gray-800">
                        <th>{t('English Label')}</th>
                        <th>{t('Arabic Label')}</th>
                        <th className="w-50px"></th>
                      </tr>
                    </thead>
                    <tbody>
                      {editingFilter.options.map((opt, index) => (
                        <tr key={index}>
                          <td>
                            <input
                              type="text"
                              className="form-control form-control-sm"
                              value={opt.en}
                              onChange={(e) => handleModalOptionChange(index, 'en', e.target.value)}
                              placeholder={t('Option (EN)')}
                            />
                          </td>
                          <td>
                            <input
                              type="text"
                              className="form-control form-control-sm"
                              value={opt.ar}
                              onChange={(e) => handleModalOptionChange(index, 'ar', e.target.value)}
                              placeholder={t('Option (AR)')}
                            />
                          </td>
                          <td className="text-end">
                            <button
                              type="button"
                              className="btn btn-icon btn-sm btn-light-danger"
                              onClick={() => removeModalOption(index)}
                            >
                              <i className="fas fa-times fs-8"></i>
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <button
                  type="button"
                  className="btn btn-sm btn-light-primary w-100"
                  onClick={addModalOption}
                >
                  <i className="fas fa-plus fs-7 me-1"></i> {t('Add Option')}
                </button>
              </div>
            )}
          </div>
        )}
      </Modal>
    </div>
  );
};

export default AdminAdCategoryCustomFilters;
